import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import Typography from '@e3dc-react/shell/Elements/Typography/Typography'

import { disconnectVehicleFromInstallation } from '../api'
import { getTranslationBasedOnEhaErrorCodes, isE3dcError } from '../functions/errors.functions'
import useUserVehicles from '../hooks/use-user-vehicles'

interface RemoveVehicleModalProps {
  onClose: () => void
  installationId: number
  vehicleId: number
  open: boolean
  title: string
  description: string
}

/**
 * Dialog component to remove disconnected vehicle.
 */
const RemoveVehicleModal: React.FC<RemoveVehicleModalProps> = props => {
  const { onClose, installationId, vehicleId, title, description } = props
  const { t } = useCustomTranslation()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()
  const { refetchVehicles } = useUserVehicles(installationId)

  const handleRemoveVehicle = async (): Promise<void> => {
    const { error, hasErrors, value } = await disconnectVehicleFromInstallation(vehicleId, installationId, authFetch)
    handleSnackbars(error, hasErrors, value)
    onClose?.()
    refetchVehicles()
  }

  const handleSnackbars = (error: AuthFetchError | Error | undefined, hasErrors: boolean, value: boolean | null): void => {
    if (error && isE3dcError(error)) {
      enqueueErrorStack(getTranslationBasedOnEhaErrorCodes(error.response?.data.errno))
    }
    if (value) {
      enqueueSuccessStack(t('emobility.vehicle-integration.disconnect-vehicle-dialog.disconnect-success'))
      onClose?.()
    }
    if (hasErrors) enqueueErrorStack(t('emobility.vehicle-integration.vehicle-dialog.disconnect-failed'))
  }

  return (
    <Dialog
      maxWidth="sm"
      title={title}
      onClose={onClose}
      actions={{
        primary: {
          name: t('common.remove'),
          onClick: () => handleRemoveVehicle(),
        },
        secondary: {
          name: t('common.cancel'),
          onClick: () => onClose(),
        },
      }}
    >
      <Typography variant="body2">{description}</Typography>
    </Dialog>
  )
}

export default RemoveVehicleModal
